import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { apiClient, API_ENDPOINTS } from '../config/api';
import { getViewerTimezone } from '../utils/dateTime';
import './DisclosureViewPage.css';

const formatDate = (isoish) => {
  if (!isoish) return '';
  try {
    const d = new Date(isoish);
    if (Number.isNaN(d.getTime())) return isoish;
    return d.toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      timeZone: getViewerTimezone(),
    });
  } catch {
    return isoish;
  }
};

const formatBytes = (bytes) => {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

const isPreviewable = (fileType) =>
  fileType === 'application/pdf' || (fileType || '').startsWith('image/');

const triggerBlobDownload = (blob, fileName) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
};

const DisclosureViewPage = () => {
  const { shareToken } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionToken = searchParams.get('session') || '';

  const [me, setMe] = useState(null);
  const [docs, setDocs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  // Document preview modal — holds the doc + its object URL once fetched.
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const previewUrlRef = useRef(null);

  const [isDownloadingAll, setIsDownloadingAll] = useState(false);
  const [downloadAllNote, setDownloadAllNote] = useState(null);
  const [isDownloadMenuOpen, setIsDownloadMenuOpen] = useState(false);
  const downloadMenuRef = useRef(null);

  const authHeaders = useCallback(() => ({ Authorization: `Bearer ${sessionToken}` }), [sessionToken]);

  useEffect(() => {
    if (!sessionToken) {
      setLoadError('This link is missing its access token. Please open the link from the verification email.');
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const [meRes, docsRes] = await Promise.all([
          apiClient.get(API_ENDPOINTS.publicDisclosureMe(shareToken), { headers: authHeaders() }),
          apiClient.get(API_ENDPOINTS.publicDisclosureDocuments(shareToken), { headers: authHeaders() }),
        ]);
        if (cancelled) return;
        setMe(meRes.data);
        setDocs(Array.isArray(docsRes.data) ? docsRes.data : []);
      } catch (e) {
        if (cancelled) return;
        const msg = e?.response?.data?.message
          || (e?.response?.status === 401 ? 'Your access has expired. Please verify again.' : 'Could not load documents.');
        setLoadError(msg);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [shareToken, sessionToken, authHeaders]);

  const handleReverify = () => {
    // Stay on the same URL prefix the recipient arrived on — folder shares
    // live under /shared/, disclosure shares under /disclosure/. Falling back
    // to /disclosure/ keeps behaviour identical to the pre-folder shipping
    // implementation when window.location isn't available for any reason.
    const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
    const prefix = currentPath.startsWith('/shared/') ? '/shared/' : '/disclosure/';
    navigate(`${prefix}${shareToken}`);
  };

  // Document downloads are already token-gated and public, but the server
  // sends Content-Disposition: attachment on them — fine for the Download
  // button, useless for an inline preview (the browser would just re-trigger
  // a save dialog). Fetching the bytes ourselves and rendering them via an
  // object URL sidesteps that regardless of the response headers.
  const openPreview = async (doc) => {
    setPreviewDoc(doc);
    setPreviewUrl(null);
    setPreviewError(null);
    if (!isPreviewable(doc.fileType)) return;
    try {
      const res = await apiClient.get(doc.downloadUrl, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: doc.fileType });
      const url = URL.createObjectURL(blob);
      previewUrlRef.current = url;
      setPreviewUrl(url);
    } catch {
      setPreviewError('Could not load a preview for this file. Try downloading it instead.');
    }
  };

  const closePreview = () => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
    setPreviewDoc(null);
    setPreviewUrl(null);
    setPreviewError(null);
  };

  useEffect(() => () => {
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
  }, []);

  // Close the Download dropdown on any outside click — mirrors the agent-side
  // DisclosureTab's dropdown pattern.
  useEffect(() => {
    if (!isDownloadMenuOpen) return undefined;
    const onDocClick = (e) => {
      if (downloadMenuRef.current && !downloadMenuRef.current.contains(e.target)) {
        setIsDownloadMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [isDownloadMenuOpen]);

  const handleDownloadAll = async () => {
    setIsDownloadingAll(true);
    setDownloadAllNote(null);
    try {
      const res = await apiClient.get(API_ENDPOINTS.publicDisclosureCombinedPdf(shareToken), {
        headers: authHeaders(),
        responseType: 'blob',
      });
      const skippedHeader = res.headers?.['x-disclosure-skipped-files'];
      const skipped = skippedHeader
        ? skippedHeader.split(',').filter(Boolean).map((s) => decodeURIComponent(s))
        : [];
      if (res.data.size === 0) {
        setDownloadAllNote('None of these documents could be combined into one PDF — download them individually below.');
      } else {
        const disposition = res.headers?.['content-disposition'] || '';
        const match = disposition.match(/filename="?([^"]+)"?/);
        triggerBlobDownload(res.data, match ? match[1] : 'disclosures.pdf');
        if (skipped.length > 0) {
          setDownloadAllNote(`Combined PDF downloaded. ${skipped.length} file${skipped.length === 1 ? '' : 's'} couldn't be included (not a PDF or image) — download separately: ${skipped.join(', ')}`);
        }
      }
    } catch (e) {
      setDownloadAllNote(e?.response?.data?.message || 'Could not build the combined PDF. Please try again.');
    } finally {
      setIsDownloadingAll(false);
    }
  };

  const handleDownloadZip = async () => {
    setDownloadAllNote(null);
    try {
      const res = await apiClient.get(API_ENDPOINTS.publicDisclosureZip(shareToken), {
        headers: authHeaders(),
        responseType: 'blob',
      });
      const disposition = res.headers?.['content-disposition'] || '';
      const match = disposition.match(/filename="?([^"]+)"?/);
      triggerBlobDownload(res.data, match ? match[1] : 'disclosures.zip');
    } catch (e) {
      setDownloadAllNote(e?.response?.data?.message || 'Could not download the ZIP. Please try again.');
    }
  };

  // Partition into pinned cover-sheet row + the regular docs the viewer sees. The
  // server already drops the cover-sheet row entirely when the agent has hidden it,
  // so no second check needed here. First match wins — in practice there's exactly
  // one cover-sheet row per folder.
  const coverSheetDoc = docs.find((d) => d.isCoverSheet);
  const regularDocs = docs.filter((d) => !d.isCoverSheet);

  // Folder shares (folder !== "disclosure") reuse the same viewer — the only
  // reason to branch is wording, so "Rialty Documents" replaces "Rialty
  // Disclosures" and "documents" replaces "disclosure documents" for anything
  // that isn't the seller-disclosure flow. Fall back to the URL prefix while
  // /me is still loading, so the loading label / brand don't briefly show
  // "disclosure" for a Transaction Documents share.
  const pathIsFolderShare = typeof window !== 'undefined'
      && window.location.pathname.startsWith('/shared/');
  const isFolderShare = me?.folder
      ? me.folder !== 'disclosure'
      : pathIsFolderShare;
  const brandLabel = isFolderShare ? 'Rialty Documents' : 'Rialty Disclosures';
  const loadingLabel = isFolderShare ? 'Loading documents…' : 'Loading disclosure documents…';
  const errorLabel = isFolderShare ? 'Could not load documents.' : 'Could not load disclosure documents.';
  const emptyLabel = isFolderShare
    ? 'No documents have been added to this share yet.'
    : 'No documents have been added to this disclosure yet.';

  return (
    <div className="disclosure-view-shell">
      <header className="disclosure-view-header">
        <div className="disclosure-view-header-inner">
          <div className="disclosure-view-brand">{brandLabel}</div>
          {me && (
            <div className="disclosure-view-greeting">
              Hi, <strong>{me.firstName || 'there'}</strong>
            </div>
          )}
        </div>
      </header>

      <main className="disclosure-view-main">
        {isLoading && <p className="disclosure-view-status">{loadingLabel}</p>}

        {!isLoading && loadError && (
          <div className="disclosure-view-error-card">
            <h2>Access issue</h2>
            <p>{loadError}</p>
            <button className="disclosure-view-primary" onClick={handleReverify}>Verify again</button>
          </div>
        )}

        {!isLoading && !loadError && me && (
          <section className="disclosure-view-package-card">
            {/* Single card frames everything a recipient sees — mockup Screen 3.
                Title combines folder-aware label + address; byline folds in the
                agent's brokerage when the server resolved one. Download dropdown
                replaces the previous inline Download All button. */}
            <div className="disclosure-view-package-header">
              <div className="disclosure-view-package-headlines">
                <h1 className="disclosure-view-package-title">
                  {isFolderShare ? 'Documents' : 'Disclosures'}
                  {me.propertyAddress ? ` – ${me.propertyAddress}` : ''}
                </h1>
                <div className="disclosure-view-package-byline">
                  Listing Agent: {me.agentName || '—'}
                  {me.agentBrokerage ? `, ${me.agentBrokerage}` : ''}
                </div>
              </div>

              <div className="disclosure-view-download-dropdown" ref={downloadMenuRef}>
                <button
                  type="button"
                  className="disclosure-view-download-trigger"
                  onClick={() => setIsDownloadMenuOpen((v) => !v)}
                  disabled={regularDocs.length === 0 && !coverSheetDoc}
                  aria-haspopup="menu"
                  aria-expanded={isDownloadMenuOpen}
                >
                  Download ▾
                </button>
                {isDownloadMenuOpen && (
                  <div className="disclosure-view-download-menu" role="menu">
                    <button
                      type="button"
                      role="menuitem"
                      className="disclosure-view-download-menu-item"
                      onClick={() => { setIsDownloadMenuOpen(false); handleDownloadAll(); }}
                      disabled={isDownloadingAll}
                    >
                      <span className="disclosure-view-download-menu-item-title">
                        {isDownloadingAll ? 'Combining…' : 'Combined PDF'}
                      </span>
                      <span className="disclosure-view-download-menu-item-subtitle">
                        One file: cover sheet first, then documents in package order
                      </span>
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      className="disclosure-view-download-menu-item"
                      onClick={() => { setIsDownloadMenuOpen(false); handleDownloadZip(); }}
                    >
                      <span className="disclosure-view-download-menu-item-title">
                        Separate PDFs (.zip)
                      </span>
                      <span className="disclosure-view-download-menu-item-subtitle">
                        Each document as its own file, cover sheet included
                      </span>
                    </button>
                  </div>
                )}
              </div>
            </div>

            {downloadAllNote && (
              <p className="disclosure-view-download-all-note">{downloadAllNote}</p>
            )}

            {/* Cover sheet is a pinned, first-child row inside the package card
                (matches the Agent view). Server flags isCoverSheet=true and omits
                the row entirely when the agent has hidden it. */}
            {coverSheetDoc && (
              <div className="disclosure-view-coversheet-row">
                <div className="disclosure-view-coversheet-left">
                  <span className="disclosure-view-coversheet-icon" aria-hidden="true">
                    {/* Document-with-lines glyph (matches mockup Screen 3 better
                        than the pin emoji — reads as a document, not a sticky). */}
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                         stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                      <polyline points="14 2 14 8 20 8"/>
                      <line x1="8" y1="13" x2="16" y2="13"/>
                      <line x1="8" y1="17" x2="13" y2="17"/>
                    </svg>
                  </span>
                  <div>
                    <div className="disclosure-view-coversheet-title">
                      Cover sheet
                      <span className="disclosure-view-coversheet-badge">Overview</span>
                    </div>
                    <div className="disclosure-view-coversheet-meta">
                      {coverSheetDoc.pageCount ? `${coverSheetDoc.pageCount} page` : 'PDF'}
                      {coverSheetDoc.generatedAt ? ` · Updated ${formatDate(coverSheetDoc.generatedAt)}` : ''}
                      <span className="disclosure-view-coversheet-hint">
                        {' · Package summary from '}{me.agentName || 'the listing agent'}
                      </span>
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  className="disclosure-view-coversheet-view"
                  onClick={() => openPreview(coverSheetDoc)}
                >
                  View
                </button>
              </div>
            )}

            {regularDocs.length === 0 && !coverSheetDoc ? (
              <p className="disclosure-view-empty">{emptyLabel}</p>
            ) : (
              <ul className="disclosure-view-doc-list">
                {regularDocs.map((d) => {
                  // "N pages · size" when both known, else fall back to whichever we have.
                  const metaParts = [];
                  if (d.pageCount) metaParts.push(`${d.pageCount} page${d.pageCount === 1 ? '' : 's'}`);
                  if (d.fileSize) metaParts.push(formatBytes(d.fileSize));
                  return (
                    <li key={d.fileId} className="disclosure-view-doc-item">
                      <div className="disclosure-view-doc-info">
                        <div className="disclosure-view-doc-name">{d.fileName}</div>
                        {metaParts.length > 0 && (
                          <div className="disclosure-view-doc-meta">{metaParts.join(' · ')}</div>
                        )}
                      </div>
                      <button
                        type="button"
                        className="disclosure-view-coversheet-view"
                        onClick={() => openPreview(d)}
                      >
                        View
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        )}
      </main>

      <footer className="disclosure-view-footer">
        <p>Documents are logged for legal audit. Please keep them confidential.</p>
      </footer>

      {previewDoc && (
        <div className="disclosure-view-modal-overlay" onClick={closePreview}>
          <div className="disclosure-view-modal" onClick={(e) => e.stopPropagation()}>
            <div className="disclosure-view-modal-header">
              <div className="disclosure-view-modal-title">{previewDoc.fileName}</div>
              <div className="disclosure-view-modal-actions">
                <a
                  className="disclosure-view-download"
                  href={previewDoc.downloadUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  download
                >
                  Download
                </a>
                <button className="disclosure-view-modal-close" onClick={closePreview} aria-label="Close">×</button>
              </div>
            </div>
            <div className="disclosure-view-modal-body">
              {!isPreviewable(previewDoc.fileType) ? (
                <p className="disclosure-view-modal-fallback">
                  Preview isn't available for this file type. Use Download above to open it.
                </p>
              ) : previewError ? (
                <p className="disclosure-view-modal-fallback">{previewError}</p>
              ) : !previewUrl ? (
                <p className="disclosure-view-modal-fallback">Loading preview…</p>
              ) : previewDoc.fileType === 'application/pdf' ? (
                <iframe className="disclosure-view-modal-pdf" src={previewUrl} title={previewDoc.fileName} />
              ) : (
                <img className="disclosure-view-modal-image" src={previewUrl} alt={previewDoc.fileName} />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DisclosureViewPage;
